import React, { FunctionComponent } from 'react'
import { FormattedMessage } from 'react-intl'
import { SafeAreaView } from 'react-native-safe-area-context'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { CalendarCheckmarkLottie } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/calendarCheckmarkLottie/CalendarCheckmarkLottie'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

interface CalendarIsLiveConfirmationModalProps {
  visible: boolean
  onClosePress: () => void
}

const Container = styled(SafeAreaView)<{ theme: ThemeType }>(({ theme }) => ({
  ...(theme.breakpoints.isMobileSized && {
    flexGrow: 1,
    paddingHorizontal: theme.spacing['16px'],
    paddingVertical: theme.spacing['12px'],
    backgroundColor: theme.colors.backgroundPrimary,
  }),
}))

const Content = styled.View({
  alignItems: 'center',
  marginTop: 0,
})

const ButtonContainer = styled.View({
  justifyContent: 'flex-end',
  flexDirection: 'row',
})

const CloseButton = styled(PrimaryButton)<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['32px'],
}))

const Heading = styled(Title)(({ theme: { spacing } }) => ({
  marginTop: spacing['16px'],
  marginBottom: spacing['8px'],
}))

export const CalendarIsLiveConfirmationModal: FunctionComponent<CalendarIsLiveConfirmationModalProps> = ({
  visible = false,
  onClosePress = () => {},
}) => {
  const theme = useTheme()
  const modalContents = (
    <Container testID={tID('CalendarIsLiveModal')}>
      <ButtonContainer>
        <PressableOpacity onPress={onClosePress}>
          <XIcon size={24} />
        </PressableOpacity>
      </ButtonContainer>
      <Content testID={tID('CalendarIsLiveModalContent')}>
        <CalendarCheckmarkLottie />
        <Heading
          textAlign='center'
          size={TitleSize.MEDIUM}
          text={
            <FormattedMessage
              defaultMessage='Your Lyra calendar is now live!'
              description='Header text for modal that informs the user they have successfully set up their Lyra calendar'
            />
          }
        />
        <BodyText
          textAlign='center'
          color={theme.colors.textSecondary}
          text={
            <FormattedMessage
              defaultMessage='Review your calendar for any individual week adjustments you may need to make.'
              description='Body text for modal that informs the user they have successfully set up their Lyra calendar'
            />
          }
        />
      </Content>
      <ButtonContainer>
        <CloseButton
          onPress={onClosePress}
          testID={tID('CalendarIsLiveModal-continueButton')}
          text={<FormattedMessage defaultMessage='Done' description='Button text. Clicking will dismiss the modal' />}
        />
      </ButtonContainer>
    </Container>
  )

  return (
    <Modal
      onRequestClose={onClosePress}
      onCloseEnd={onClosePress}
      visible={visible}
      modalContents={modalContents}
      useDynamicHeightForBottomSheet
      showCloseIcon
    />
  )
}
